import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { User } from 'app/core/user/user.types';
import { environment } from 'environments/environment';
import { map, Observable, of, ReplaySubject, switchMap, tap } from 'rxjs';
import { Storage } from '@targx/libs/storage';
import { GET_WhoAmI_QUERY } from '@targx/graphql-queries/user';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private _httpClient = inject(HttpClient);
  private apollo = inject(Apollo);

  private _user: ReplaySubject<User> = new ReplaySubject<User>(1);
  public _currentUser: User;
  public _roles: User;

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
     * Setter & getter for user
     *
     * @param value
     */
  set user(value: User) {
    // Store the value
    this._currentUser = value;
    this._user.next(value);
  }

  get user$(): Observable<User> {
    return this._user.asObservable();
  }


  set roles(value: any) {
    this._roles = value;
  }

  get roles() {
    return (
      this._roles ||
      JSON.parse(Storage.get(environment.KEY_LOCAL_STORAGE_ROLES))
    );
  }

  hasRole(roles: string[]): boolean {
    if (!this._currentUser) {
      return false;
    }

    return roles.some((role: string) => this.roles.includes(role));
  }

  validateRole(roles: string[]): boolean {
    if (!this._currentUser) {
      return false;
    }

    return roles.some((role: string) => this.roles.includes(role));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------


  /**
   * Get the current logged in user data
   */
  get(): Observable<any> {
    return this.apollo
      .mutate<any>({
        mutation: GET_WhoAmI_QUERY,
      })
      .pipe(
        switchMap(({ data }) => {
          this.user = data.AuthWhoAmI;
          return of(data.AuthWhoAmI);
        })
      );
  }


  /**
   * Update the user
   *
   * @param user
   */
  update(user: User): Observable<any> {
    return this._httpClient.patch<User>('api/common/user', { user }).pipe(
      map((response) => {
        this._user.next(response);
      }),
    );
  }
}
